import { Star } from "lucide-react";
import { FieldTitle, useInput, type InputProps } from "ra-core";
import { FormError, FormField, FormLabel } from "@/components/admin/form";
import { cn } from "@/lib/utils";

import { ToDefine } from "./ToDefine";

const SCORES = [1, 2, 3, 4, 5];

/** Read-only 1 to 5 stars, or « À définir » when empty. */
export const RatingStars = ({ value }: { value?: number | null }) => {
  if (!value) return <ToDefine />;
  return (
    <span className="inline-flex items-center gap-0.5" title={`${value}/5`}>
      {SCORES.map((score) => (
        <Star
          key={score}
          className={cn(
            "size-4",
            score <= value
              ? "fill-amber-400 text-amber-400"
              : "text-muted-foreground/40",
          )}
        />
      ))}
    </span>
  );
};

/** 1 to 5 stars input. Clicking the current score clears it (the field is never required). */
export const RatingInput = (props: InputProps & { className?: string }) => {
  const { className, label, source, resource } = props;
  const { id, field, isRequired } = useInput(props);
  const current = Number(field.value) || 0;

  return (
    <FormField className={className} id={id} name={field.name}>
      <FormLabel htmlFor={id}>
        <FieldTitle
          label={label}
          source={source}
          resource={resource}
          isRequired={isRequired}
        />
      </FormLabel>
      <div className="flex items-center gap-1">
        {SCORES.map((score) => (
          <button
            key={score}
            type="button"
            aria-label={`${score}/5`}
            aria-pressed={score <= current}
            onClick={() => {
              field.onChange(score === current ? null : score);
              field.onBlur();
            }}
            className="p-0.5 rounded hover:bg-muted"
          >
            <Star
              className={cn(
                "size-5",
                score <= current
                  ? "fill-amber-400 text-amber-400"
                  : "text-muted-foreground/50",
              )}
            />
          </button>
        ))}
      </div>
      <FormError />
    </FormField>
  );
};
